import type React from 'react'
import type { RailCell, RailCurve } from './LineDiagram'
import styles from './LineRail.module.css'
import { RailGeometry } from './railGeometry'

const railX = RailGeometry.x

export interface LineRailProps {
  readonly cells: readonly RailCell[]
  readonly curves?: readonly RailCurve[]
  /** Row height in px — the rail fills it exactly. */
  readonly height: number
  readonly color: string
  /** Hollow bar (DLR / Overground / Trams): a 4 px cutout stripe inside the 8 px bar. */
  readonly outlined: boolean
}

/**
 * One row's slice of the line view's rail diagram, drawn to the route-detail
 * row spec (RD/Row-FirstStop · MiddleStop · LastStop, Figma `6900:24583`):
 * 8 px bar per rail; a rail's top terminus a 28 ring (16 cutout), its bottom
 * terminus a 16 dot (8 cutout); every other stop a 6×4 tick on the bar —
 * cutout-coloured, or line-coloured across an outlined bar's stripe.
 * Split / merge curves join a junction station to a side rail.
 */
export function LineRail({
  cells,
  curves = [],
  height,
  color,
  outlined,
}: LineRailProps): React.ReactElement {
  const mid = height / 2
  const spans: { rail: number; from: number; to: number }[] = []
  cells.forEach((cell, rail) => {
    if (cell === 'pass' || cell === 'stop') spans.push({ rail, from: 0, to: height })
    if (cell === 'start') spans.push({ rail, from: mid, to: height })
    if (cell === 'end') spans.push({ rail, from: 0, to: mid })
  })
  // Control points to 0.1 px — enough for an 8 px stroke, and stable text.
  const at = (fraction: number): number => Math.round(mid * fraction * 10) / 10
  const curvePaths = curves.map(({ dir, from, to }) => {
    const a = railX(from)
    const b = railX(to)
    return dir === 'in'
      ? `M ${a} 0 C ${a} ${at(0.7)} ${b} ${at(0.4)} ${b} ${mid}`
      : `M ${a} ${mid} C ${a} ${at(1.6)} ${b} ${at(1.3)} ${b} ${height}`
  })
  return (
    <svg
      className={styles.rail}
      width={RailGeometry.width(cells.length)}
      height={height}
      viewBox={`0 0 ${RailGeometry.width(cells.length)} ${height}`}
      aria-hidden="true"
      data-outlined={outlined ? 'true' : 'false'}
    >
      {spans.map(({ rail, from, to }) => (
        <rect
          key={`bar-${rail}`}
          x={railX(rail) - 4}
          y={from}
          width={8}
          height={to - from}
          fill={color}
        />
      ))}
      {curvePaths.map((d) => (
        <path key={`curve-${d}`} d={d} fill="none" stroke={color} strokeWidth={8} />
      ))}
      {outlined &&
        spans.map(({ rail, from, to }) => (
          <rect
            key={`stripe-${rail}`}
            className={styles.cutout}
            x={railX(rail) - 2}
            y={from}
            width={4}
            height={to - from}
          />
        ))}
      {outlined &&
        curvePaths.map((d) => (
          <path
            key={`stripe-${d}`}
            className={styles.cutoutStroke}
            d={d}
            fill="none"
            strokeWidth={4}
          />
        ))}
      {cells.map((cell, rail) => {
        const x = railX(rail)
        if (cell === 'stop') {
          return (
            <rect
              key={`marker-${rail}`}
              data-marker="stop"
              className={outlined ? undefined : styles.cutout}
              x={x - 1}
              y={mid - 2}
              width={6}
              height={4}
              fill={outlined ? color : undefined}
            />
          )
        }
        if (cell === 'start' || cell === 'end') {
          const [outer, inner] = cell === 'start' ? [14, 8] : [8, 4]
          return (
            <g key={`marker-${rail}`} data-marker={cell}>
              <circle cx={x} cy={mid} r={outer} fill={color} />
              <circle className={styles.cutout} cx={x} cy={mid} r={inner} />
            </g>
          )
        }
        return null
      })}
    </svg>
  )
}
